import { Deal } from "../models/shared/Deal";
import { OfferAuditRecordType } from "../models/shared/OfferAuditRecord";
import { IntegrationType, OrganizationPublic } from "../models/shared/Organization";
import { PlaceCandidate } from "../models/shared/PlaceCandidate";
import { SearchResults } from "../models/shared/SearchResults";
import { ShareToken } from "../models/shared/ShareToken";
import { User, UserClaims } from "../models/shared/User";
import config from "./config"
import { initialSignupSource } from "./analytics";
import { COLLECTIONS, ORGANIZATION_SETTINGS_TABS, PlanId, QUERY_PARAMS } from "./shared/constants";
import { MAP_RENDER_API_KEY } from "./shared/constants";
import { DealPublicInputWithPlace } from "../models/shared/DealPublicInput";
import { Attachement as Attachment } from "../models/shared/Attachment";
import { UserRole } from "../models/shared/UserInvitation";
import { CreateOrganizationData, DeleteOrganizationData, PlanUpdateData, TransferOwnershipData, UpdateUserRoleData } from "../models/shared/api";


export const loginInit = async (email: string) => {
    const endpoint = config.functions.loginInit;
    // The native app's origin (capacitor://localhost, https://localhost) can't be opened from an email, so let the backend use the frontend URL
    const domain = config.isNativeApp ? undefined : window.location.origin;
    const result = await fetch(endpoint, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, domain, signupSource: initialSignupSource })
    });

    if (result.status !== 200) {
        throw new Error('Failed to initiate login');
    }
}

interface LoginVerifyResponse {
    customToken: string;
    uid: string;
    email: string;
}

export const loginVerify = async (token: string, language?: string): Promise<LoginVerifyResponse> => {
    const endpoint = config.functions.loginVerify;

    const result = await fetch(endpoint, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token, language })
    });

    if (result.status !== 200) {
        throw new Error('Failed to verify login');
    }

    return result.json();
}

export const loginVerifyWithCode = async (email: string, code: string, language?: string): Promise<LoginVerifyResponse> => {
    const endpoint = config.functions.loginVerifyWithCode;

    const result = await fetch(endpoint, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, code, language })
    });

    if (result.status !== 200) {
        throw new Error('Failed to verify login code');
    }

    return result.json();
}

export const postLoginVerify = async (userToken: string, language?: string) => {
    const endpoint = config.functions.postLoginVerify;

    await fetch(endpoint, {
        method: 'POST',
        headers: {
            authorization: `Bearer ${userToken}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ language }),
    });
}

export const createUserInvitation = async (userToken: string, email: string, orgId: string, role: UserRole) => {
    const endpoint = config.functions.createUserInvitation;

    const result = await fetch(endpoint, {
        method: 'POST',
        headers: {
            authorization: `Bearer ${userToken}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, orgId, role })
    });
    if (result.status !== 200) {
        throw new Error('Failed to create user invitation');
    }
}

export const deleteUserInvitation = async (userToken: string, orgId: string, invitationId: string) => {
    const endpoint = config.functions.deleteUserInvitation;

    const result = await fetch(endpoint, {
        method: 'POST',
        headers: {
            authorization: `Bearer ${userToken}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ orgId, invitationId })
    });

    if (result.status !== 200) {
        throw new Error('Failed to delete user invitation');
    }
}

export const updateUserRole = async (userToken: string, orgId: string, invitationId: string, role: UserRole) => {
    const payload: UpdateUserRoleData = { orgId, invitationId, role };

    const result = await fetch(config.functions.updateUserRole, {
        method: 'POST',
        headers: {
            authorization: `Bearer ${userToken}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
    });

    if (result.status !== 200) {
        throw new Error('Failed to update user role');
    }
}

export const setMemberActive = async (userToken: string, orgId: string, invitationId: string, active: boolean) => {
    const payload: UpdateUserRoleData = { orgId, invitationId, active };

    const result = await fetch(config.functions.updateUserRole, {
        method: 'POST',
        headers: {
            authorization: `Bearer ${userToken}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
    });

    if (result.status !== 200) {
        throw new Error('Failed to update member status');
    }
}

const PLACES_API = `https://places.googleapis.com/v1/places:searchText`

export const searchPlaces = async (userToken: string, query: string): Promise<PlaceCandidate[]> => {
    if (!query || query.trim() === '') {
        return [];
    }
    try {
        const endpoint = `${PLACES_API}?key=${MAP_RENDER_API_KEY}&fields=places.id,places.displayName,places.formattedAddress`;

        const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ textQuery: query }),
        });

        if (!response.ok) {
            throw new Error('Failed to fetch places');
        }

        const data = await response.json();

        const placeCandidates: PlaceCandidate[] = data.places?.map((place: any) => ({
            placeId: place.id,
            formattedAddress: place.formattedAddress,
            displayName: place.displayName.text,
        })) || [];


        return placeCandidates;
    } catch (error) {
        console.error('Error searching places', { error });
        throw new Error('Failed to search places');
    }
}

export const getGeocodingForPlaceId = async (userToken: string, placeId: string) => {
    const endpoint = config.functions.getGeocodingForPlaceId;

    const result = await fetch(endpoint, {
        method: 'POST',
        headers: {
            authorization: `Bearer ${userToken}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ placeId })
    });

    if (result.status !== 200) {
        throw new Error('Failed to get geocoding');
    }

    const response = await result.json();
    return response.geocoding;
}

interface GetSuperUserInfoResult {
    user: User,
    claims: UserClaims,
}
export const getSuperUserInfo = async (userToken: string, userEmail: string): Promise<GetSuperUserInfoResult> => {
    const endpoint = config.functions.getSuperUserInfo;

    const result = await fetch(endpoint, {
        method: 'POST',
        headers: {
            authorization: `Bearer ${userToken}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userEmail })
    });

    if (result.status !== 200) {
        throw new Error('Failed to get super user info');
    }

    const response = await result.json();
    return response;
}

interface SearchOptions {
    type?: COLLECTIONS.CUSTOMERS | COLLECTIONS.DEALS;
}
interface SearchResponse {
    hits: SearchResults
}

export const search = async (userToken: string, query: string, orgId: string, options: SearchOptions = {}): Promise<SearchResponse> => {
    const endpoint = config.functions.search;

    const params = new URLSearchParams({
        query,
        orgId,
        type: options.type || ''
    }).toString();

    const result = await fetch(`${endpoint}?${params}`, {
        headers: {
            authorization: `Bearer ${userToken}`,
        }
    });

    if (result.status !== 200) {
        throw new Error('Failed to search');
    }

    const response = await result.json();
    return response;
}

interface GetDataFromTokenResponse {
    deal: Deal;
    organization: OrganizationPublic;
    shareToken: ShareToken;
}
export const getDataFromToken = async (token: string) => {
    const endpoint = config.functions.getDataFromToken;

    const result = await fetch(endpoint, {
        method: 'POST',
        headers: {
            authorization: `Bearer ${token}`,
        },
    });

    if (result.status !== 200) {
        throw new Error('Failed to get data from token');
    }
    const response = await result.json();
    return response as GetDataFromTokenResponse;
}

export const actionOffer = async (token: string, action: OfferAuditRecordType, comment?: string) => {
    const endpoint = config.functions.actionOffer;

    const result = await fetch(endpoint, {
        method: 'POST',
        body: JSON.stringify({ action, comment }),
        headers: {
            'Content-Type': 'application/json',
            authorization: `Bearer ${token}`
        }
    })
    if (result.status !== 200) {
        throw new Error('Failed to action offer');
    }
}

export const createDealFromForm = async (input: DealPublicInputWithPlace) => {
    const endpoint = config.functions.createDealFromForm;

    const result = await fetch(endpoint, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(input)
    });

    if (result.status !== 200) {
        throw new Error('Failed to create deal');
    }

    const { dealId } = await result.json();
    return dealId;
}

export const addAttachementsToDeal = async (orgId: string, dealId: string, attachments: Attachment[]) => {
    const endpoint = config.functions.addAttachmentsToDeal;

    const result = await fetch(endpoint, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ orgId, dealId, attachments })
    });

    if (result.status !== 200) {
        throw new Error('Failed to add attachements to deal');
    }

    return await result.json();
}

export const getGoogleMapsShortlinkLocation = async (shortUrl: string) => {
    const endpoint = config.functions.getGoogleMapsShortlinkLocation;

    const params = new URLSearchParams({ shortUrl }).toString();
    const result = await fetch(`${endpoint}?${params}`);

    if (result.status !== 200) {
        throw new Error('Failed to get location from short URL');
    }

    const response = await result.json();
    return response.finalUrl;
}

interface InitiatePlanUpdateResponse {
    url: string;
}

export const initiatePlanUpdate = async (token: string, orgId: string, planId: PlanId, annually: boolean): Promise<InitiatePlanUpdateResponse> => {
    const returnUrl = `${window.location.origin}${window.location.pathname}?${QUERY_PARAMS.TAB}=${ORGANIZATION_SETTINGS_TABS.BILLING}`;
    const payload: PlanUpdateData = {
        orgId,
        planId,
        annually,
        returnUrl,
    }
    const endpoint = config.functions.initiatePlanUpdate;

    const result = await fetch(endpoint, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
    });
    if (!result.ok) {
        throw new Error(await result.text());
    }

    return result.json();
};

interface CreateOrganizationResponse {
    orgId: string;
}

export const createOrganization = async (token: string, name: string, language?: string): Promise<CreateOrganizationResponse> => {
    const payload: CreateOrganizationData = { name, language };

    const result = await fetch(config.functions.createOrganization, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
    });
    if (!result.ok) {
        throw new Error(await result.text());
    }

    return result.json();
};

export const deleteOrganization = async (token: string, orgId: string): Promise<void> => {
    const payload: DeleteOrganizationData = { orgId };

    const result = await fetch(config.functions.deleteOrganization, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
    });
    if (!result.ok) {
        throw new Error(await result.text());
    }
};

export const transferOwnership = async (token: string, orgId: string, newOwnerEmail: string): Promise<void> => {
    const payload: TransferOwnershipData = { orgId, newOwnerEmail };

    const result = await fetch(config.functions.transferOwnership, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
    });
    if (!result.ok) {
        throw new Error(await result.text());
    }
};

export const syncAccountingRecord = async (token: string, orgId: string, entityType: 'customer', id: string, direction: 'in' | 'out'): Promise<void> => {
    const result = await fetch(config.functions.syncAccountingRecord, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ orgId, entityType, id, direction }),
    });
    if (!result.ok) {
        throw new Error(await result.text());
    }
};
export const integrationOauth = async (token: string, orgId: string, integrationType: IntegrationType, code?: string) => {
    const endpoint = config.functions.integrationOauth;

    const result = await fetch(endpoint, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ orgId, integrationType, code }),
    });

    if (!result.ok) {
        throw new Error(await result.text());
    }

    return result.json() as Promise<{ redirectUrl?: string }>;
};

export const syncAccountingCustomers = async (token: string, orgId: string): Promise<{ imported: number; linked: number; pushed: number }> => {
    const endpoint = config.functions.syncAccounting;

    const result = await fetch(endpoint, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ orgId }),
    });

    if (!result.ok) {
        throw new Error(await result.text());
    }

    return result.json();
};